import { useEffect, useRef, useState } from 'react';

const DAY_MS = 86_400_000;

export function formatSaleTimeLeft(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const days = Math.floor(totalSeconds / 86_400);
  const hours = Math.floor((totalSeconds % 86_400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  if (days > 0) return `${days}D ${pad(hours)}H`;
  if (hours > 0) return `${hours}H ${pad(minutes)}M`;
  return `${minutes}M ${pad(seconds)}S`;
}

/** Milliseconds until `endsAt` (ticks every second in the final day); calls `onExpire` once at zero. */
export function useSaleTimeLeft(endsAt: string | null | undefined, onExpire?: () => void): number | null {
  const endMs = endsAt ? Date.parse(endsAt) : NaN;
  const [now, setNow] = useState(() => Date.now());
  const expiredRef = useRef(false);
  const onExpireRef = useRef(onExpire);
  onExpireRef.current = onExpire;

  const left = Number.isFinite(endMs) ? endMs - now : null;
  const tickMs = left != null && left < DAY_MS ? 1000 : 30_000;

  useEffect(() => {
    if (!Number.isFinite(endMs)) return;
    expiredRef.current = false;
    const id = window.setInterval(() => setNow(Date.now()), tickMs);
    return () => window.clearInterval(id);
  }, [endMs, tickMs]);

  useEffect(() => {
    if (left != null && left <= 0 && !expiredRef.current) {
      expiredRef.current = true;
      onExpireRef.current?.();
    }
  }, [left]);

  return left;
}

function ClockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} width="11" height="11" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.5" />
      <path d="M12 7v5l3 2" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}

interface SaleCountdownProps {
  endsAt: string;
  onExpire?: () => void;
  className?: string;
}

export default function SaleCountdown({ endsAt, onExpire, className = '' }: SaleCountdownProps) {
  const left = useSaleTimeLeft(endsAt, onExpire);
  if (left == null || left <= 0) return null;
  return (
    <span
      className={`inline-flex items-center gap-1 font-anton text-[11px] uppercase leading-none tabular-nums ${className}`}
      aria-label={`Sale ends in ${formatSaleTimeLeft(left)}`}
    >
      <ClockIcon className="shrink-0" />
      ENDS IN {formatSaleTimeLeft(left)}
    </span>
  );
}
